/**
 * <BrandMark> — official АРИС logo, faithful to design-reference.
 *
 * Anatomy (per design-reference/extracted/index.html L436-450):
 *
 *   .brand            ← optional flex wrapper when withText/subText is set
 *   ├── .brand-mark   ← 48×48 dark-ink rounded square; white "A" centered
 *   └── .brand-text   ← (when withText) <h1>АРИС · Платформа</h1> + .sub
 *
 * The mark itself is a plain styled <div> rather than an inline SVG: the
 * design treats the "A" as a typographic glyph (Inter 800 20px, white on
 * --ink), not a vector logotype. Rendering it as text keeps it crisp at
 * every DPI and means the brand color (--ink) is the single source of
 * truth — no SVG fill to keep in sync.
 *
 * The wordmark "АРИС · Платформа" is HTML text styled with `font-serif`
 * (mapped to Fraunces in tailwind.config.ts). Fraunces ships Latin only —
 * the Cyrillic letters fall back to the system serif. That fallback is
 * intentional and matches what the reference HTML produces in a browser.
 *
 * Sizes:
 *   - sm  → 32×32 mark (compact topbar slot)
 *   - md  → 48×48 mark (default — exactly matches the design)
 *   - lg  → 64×64 mark (auth-layout hero / marketing surfaces)
 *
 * Accessibility:
 *   - the `<svg>`-style API is gone; instead the wrapping element carries
 *     role="img" + aria-label="АРИС" so existing tests / Playwright
 *     `getByLabelText('АРИС')` selectors keep working.
 *   - `decorative` opt-in still hides the mark from a11y for sibling-text
 *     usages.
 */
import * as React from 'react';

import { cn } from '@/lib/utils';

type BrandSize = 'sm' | 'md' | 'lg';

interface SizePreset {
  /** Wrapping square dimensions. */
  mark: string;
  /** Inner glyph font-size. */
  glyph: string;
  /** Border-radius bucket. */
  radius: string;
  /** Wordmark <h1> font-size + letter-spacing. */
  title: string;
  /** Sub-line font-size. */
  sub: string;
  /** Gap between mark and text. */
  gap: string;
}

// Sizes are tuned to match the design reference:
//   md: 48×48 mark, radius 12, glyph ≈ 20px Inter 800, title ≈ 28px Fraunces.
//   sm scales ~0.67×; lg scales ~1.33×. Subtitle stays compact across sizes.
const SIZE_PRESETS: Record<BrandSize, SizePreset> = {
  sm: {
    mark: 'h-8 w-8',
    glyph: 'text-[14px]',
    radius: 'rounded-lg',
    title: 'text-[19px]',
    sub: 'text-[11.5px]',
    gap: 'gap-2.5',
  },
  md: {
    mark: 'h-12 w-12',
    glyph: 'text-[20px]',
    radius: 'rounded-xl',
    title: 'text-[28px]',
    sub: 'text-[13.5px]',
    gap: 'gap-[14px]',
  },
  lg: {
    mark: 'h-16 w-16',
    glyph: 'text-[28px]',
    radius: 'rounded-2xl',
    title: 'text-[36px]',
    sub: 'text-sm',
    gap: 'gap-4',
  },
};

export interface BrandMarkProps extends React.HTMLAttributes<HTMLDivElement> {
  size?: BrandSize;
  /** Render the "АРИС · Платформа" wordmark next to the square. */
  withText?: boolean;
  /**
   * Optional sub-line under the wordmark
   * (e.g. "Платформа управления проектами"). Implies `withText`.
   */
  subText?: string;
  /** When the mark is purely decorative (sibling text labels it). */
  decorative?: boolean;
}

export function BrandMark({
  size = 'md',
  withText = false,
  subText,
  decorative = false,
  className,
  ...rest
}: BrandMarkProps): React.ReactElement {
  const preset = SIZE_PRESETS[size];
  const showText = withText || Boolean(subText);

  // The 48×48 (or scaled) dark square with a white "A" centered.
  //
  // Live-VPS UI review (2026-05-08, defect #4): on a 375px-wide viewport
  // the wordmark sibling steals horizontal space from the mark, leaving
  // it at ~50.7×64 — a non-square rectangle. `shrink-0` pins the mark's
  // intrinsic width so flex compression touches the wordmark instead.
  // The square's `width === height` invariant is asserted in the spec.
  const mark = (
    <span
      aria-hidden={showText || decorative ? true : undefined}
      data-testid="brand-mark-square"
      className={cn(
        'inline-grid place-items-center bg-ink text-white select-none shrink-0',
        // tracking-tighter ≈ -0.05em; design specifies -0.02em — close
        // enough for a single uppercase glyph that has no kerning peers.
        'font-extrabold tracking-tight font-sans leading-none',
        preset.mark,
        preset.radius,
        preset.glyph,
      )}
    >
      A
    </span>
  );

  // Sole-mark variant: keep the existing svg-shaped a11y contract
  // (role=img + aria-label="АРИС") on the wrapping <div> so existing
  // selectors like getByLabelText('АРИС') and the live-vps Playwright
  // assertion `aria-label === 'АРИС'` keep passing.
  if (!showText) {
    return (
      <div
        data-testid="brand-mark"
        role={decorative ? 'presentation' : 'img'}
        aria-label={decorative ? undefined : 'АРИС'}
        aria-hidden={decorative ? true : undefined}
        className={cn('inline-flex items-center', className)}
        {...rest}
      >
        {mark}
      </div>
    );
  }

  // Composite mark + wordmark variant.
  return (
    <div
      data-testid="brand-mark"
      role={decorative ? 'presentation' : 'img'}
      aria-label={decorative ? undefined : 'АРИС · Платформа'}
      aria-hidden={decorative ? true : undefined}
      className={cn('flex items-center', preset.gap, className)}
      {...rest}
    >
      {mark}
      <div data-testid="brand-text" className="flex flex-col">
        <h1
          // Fraunces 600 for Latin glyphs and the "·" separator; Cyrillic
          // gracefully falls back to the system serif (intentional —
          // matches design-reference behavior).
          className={cn(
            'font-serif font-semibold leading-none tracking-tight text-ink',
            preset.title,
            subText ? 'mb-1.5' : 'm-0',
          )}
        >
          АРИС · Платформа
        </h1>
        {subText ? (
          <span data-testid="brand-subtext" className={cn('font-medium text-muted', preset.sub)}>
            {subText}
          </span>
        ) : null}
      </div>
    </div>
  );
}
